'use client';

import { c as _c } from "react/compiler-runtime";
import { useMediaQuery } from '@morpho-org/ui-v6/hooks';
import { cn } from '@morpho-org/ui-v6/utils';
import { useIsMounted } from '@repo/hooks';
import { BREAKPOINTS_PX } from '@repo/ui/breakpoints';
import Highcharts, { type Options, type YAxisPlotLinesOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useEffect, useState } from 'react';
import type { ChartState } from '../helpers/chart';
import type { StatusesChart } from '../types/chart';
import { pushPoint0InSeriesWithOnePoint } from '../helpers/highcharts';
import { ChartAreaGradients } from './ChartAreaGradients';
import ChartStatusError from './ChartStatusError';
import ChartStatusLoading from './ChartStatusLoading';
import ChartStatusNoData from './ChartStatusNoData';

// Highcharts styled mode requires its base stylesheet (default geometry:
// `.highcharts-graph { fill: none }`, stroke/area/marker defaults). Import it
// BEFORE charts.css so the @repo-palette overrides win.
import 'highcharts/css/highcharts.css';
import '../charts.css';
export interface ChartProps {
  options: Options;
  statuses: StatusesChart;
  height?: string;
  borderRadius?: number;
  hideBorder?: boolean;
  unit?: string;
  flexGrow?: number;
  hideYAxis?: boolean;
  hideXAxis?: boolean;
  className?: string;
  /**
   * Accepted for compatibility with `ChartBaseProps` spreads (V5 forwarded it
   * to the root element where Emotion dropped it). Plot lines are applied via
   * `options.yAxis.plotLines` by the chart variants, not here.
   */
  plotlinesY?: YAxisPlotLinesOptions[];
}
export function Chart(t0) {
  const $ = _c(52);
  let className;
  let flexGrow;
  let hideBorder;
  let hideXAxis;
  let hideYAxis;
  let options;
  let props;
  let statuses;
  let t1;
  let t2;
  let unit;
  if ($[0] !== t0) {
    const {
      options: t3,
      statuses: t4,
      height: t5,
      borderRadius: t6,
      unit: t7,
      flexGrow: t8,
      hideBorder: t9,
      hideYAxis: t10,
      hideXAxis: t11,
      className: t12,
      plotlinesY: _plotlinesY,
      ...t13
    } = t0;
    options = t3;
    statuses = t4;
    t1 = t5;
    t2 = t6;
    unit = t7;
    flexGrow = t8;
    hideBorder = t9;
    hideYAxis = t10;
    hideXAxis = t11;
    className = t12;
    props = t13;
    $[0] = t0;
    $[1] = className;
    $[2] = flexGrow;
    $[3] = hideBorder;
    $[4] = hideXAxis;
    $[5] = hideYAxis;
    $[6] = options;
    $[7] = props;
    $[8] = statuses;
    $[9] = t1;
    $[10] = t2;
    $[11] = unit;
  } else {
    className = $[1];
    flexGrow = $[2];
    hideBorder = $[3];
    hideXAxis = $[4];
    hideYAxis = $[5];
    options = $[6];
    props = $[7];
    statuses = $[8];
    t1 = $[9];
    t2 = $[10];
    unit = $[11];
  }
  const height = t1 === undefined ? "200px" : t1;
  const borderRadius = t2 === undefined ? 5 : t2;
  const isLargeScreen = useMediaQuery(`(min-width: ${BREAKPOINTS_PX.md}px)`);
  const isMounted = useIsMounted();
  const state = statuses.loading || !isMounted ? "loading" : statuses.error ? "error" : !options.series || options.series.every(_temp) ? "no-data" : "ready";
  let t3;
  if ($[12] !== options || $[13] !== state) {
    t3 = state === "ready" ? pushPoint0InSeriesWithOnePoint(options) : options;
    $[12] = options;
    $[13] = state;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const _options = t3;
  const [key, setKey] = useState(0);
  let t4;
  let t5;
  if ($[15] !== isLargeScreen) {
    t4 = () => {
      let resizeTimeout = null;
      const handleWindowResize = () => {
        if (resizeTimeout !== null) {
          return;
        }
        resizeTimeout = requestAnimationFrame(() => {
          setKey(_temp2);
          resizeTimeout = null;
        });
      };
      if (isLargeScreen) {
        window.addEventListener("resize", handleWindowResize);
      }
      return () => {
        if (resizeTimeout !== null) {
          window.cancelAnimationFrame(resizeTimeout);
        }
        window.removeEventListener("resize", handleWindowResize);
      };
    };
    t5 = [isLargeScreen];
    $[15] = isLargeScreen;
    $[16] = t4;
    $[17] = t5;
  } else {
    t4 = $[16];
    t5 = $[17];
  }
  useEffect(t4, t5);
  const t6 = `chart-${key}`;
  let t7;
  if ($[18] !== className) {
    t7 = cn("relative grid w-full min-w-0", className);
    $[18] = className;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== flexGrow || $[21] !== height) {
    t8 = {
      height,
      flex: flexGrow
    };
    $[20] = flexGrow;
    $[21] = height;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== borderRadius || $[24] !== state) {
    t9 = state === "error" && <ChartStatusError borderRadius={borderRadius} />;
    $[23] = borderRadius;
    $[24] = state;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== borderRadius || $[27] !== hideBorder || $[28] !== hideYAxis || $[29] !== state || $[30] !== unit) {
    t10 = state === "no-data" && <ChartStatusNoData borderRadius={borderRadius} unit={unit} hideBorder={hideBorder} hideYAxis={hideYAxis} />;
    $[26] = borderRadius;
    $[27] = hideBorder;
    $[28] = hideYAxis;
    $[29] = state;
    $[30] = unit;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== borderRadius || $[33] !== hideXAxis || $[34] !== hideYAxis || $[35] !== state) {
    t11 = state === "loading" && <ChartStatusLoading borderRadius={borderRadius} hideXAxis={hideXAxis} hideYAxis={hideYAxis} />;
    $[32] = borderRadius;
    $[33] = hideXAxis;
    $[34] = hideYAxis;
    $[35] = state;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== _options || $[38] !== hideBorder || $[39] !== options || $[40] !== state) {
    t12 = state === "ready" && <><ChartAreaGradients /><HighchartsReact options={_options} highcharts={Highcharts} containerProps={{
        className: cn("hc-styled-chart", hideBorder && "hc-hide-border", Boolean(options.chart?.plotBackgroundColor) && "hc-plot-bg"),
        style: {
          height: "100%",
          width: "100%"
        }
      }} /></>;
    $[37] = _options;
    $[38] = hideBorder;
    $[39] = options;
    $[40] = state;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== props || $[43] !== state || $[44] !== t10 || $[45] !== t11 || $[46] !== t12 || $[47] !== t6 || $[48] !== t7 || $[49] !== t8 || $[50] !== t9) {
    t13 = <div key={t6} className={t7} style={t8} data-testid="chart" data-chart-state={state} {...props}>{t9}{t10}{t11}{t12}</div>;
    $[42] = props;
    $[43] = state;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
}
function _temp2(prevKey) {
  return prevKey + 1;
}
function _temp(serie) {
  return !serie.data || serie.data.length === 0;
}